import RouteTemplate from 'ember-route-template';

import { concat } from '@ember/helper';
import Component from '@glimmer/component';
import LinksTo from 'ember-route-state/components/links-to.gjs';
import { pageTitle } from 'ember-page-title';

import CButton from 'rise-ui/components/c-button.gjs';
import CField from 'rise-ui/components/c-field.gjs';
import ExpandingBlock from 'rise-ui/components/expanding-block.gjs';
import linkToMapForItem from 'rise-ui/helpers/link-to-map-for-item.js';
import date from 'rise-ui/helpers/date.js';
import not from 'rise-ui/helpers/not.js';
import oneString from 'rise-ui/helpers/one-string.js';
import or from 'rise-ui/helpers/or.js';
import string from 'rise-ui/helpers/string.js';
import titleCase from 'rise-ui/helpers/title-case.js';
import timeSeriesSelect from 'rise-ui/helpers/time-series-select.js';
import groupItemModels from 'rise-ui/utils/group-item-models.js';
import { readOnly } from 'rise-ui/utils/object/computed.js';

export default RouteTemplate(class CatalogRecordItemTemplate extends Component {
  @readOnly('@model.otherItemsForCatalogRecord') otherItems;

  get otherGroupedItems() {
    return groupItemModels(this.otherItems);
  }

  <template>
    {{pageTitle (concat "Item " @model.item.id)}}
    {{pageTitle @model.item.title}}

    <div class="id-catalog-item">
      <h3 class="c-page-title id-catalog-item__page-title qa-page-title">{{titleCase (string "catalog-item")}}</h3>
      <h3 class="id-catalog-item__title qa-item-title">{{@model.item.title}}</h3>
      <div class="id-catalog-item__description qa-item-description">{{@model.item.description}}</div>
      {{#if false}}
      <div class="id-catalog-item__meta">

        <div class="id-catalog-item__thumb
            {{if @model.item.icon (concat "id-catalog-item__thumb--" @model.item.icon)}}
          ">
        </div>

        <div class="id-catalog-item__other">
          <h4 class="c-section-title id-catalog-item__other-label">
            {{string "item-record-items-heading"}}
          </h4>
          {{#each this.otherGroupedItems.ordered as | groupedItems |}}
            <div class="id-catalog-item__other-item-group">
              <span class="c-related-links__title">{{groupedItems.type}}</span>
              <div class="c-related-links__links">
                {{#each groupedItems.items as | otherItem |}}
                  <div class="c-related-links__link">
                    <LinksTo @route="catalog.record.item" @model={{otherItem.id}}>
                      {{~otherItem.title~}}
                    </LinksTo>
                  </div>
                {{/each}}
              </div>
            </div>
          {{else}}
            <CField/>
          {{/each}}
        </div>

      </div>
      {{/if}}

      <div class="id-catalog-item__data">
        {{#if @model.item.isTimeSeries}}
          <CButton @classMix="id-catalog-item__button--query-data"
            @disabled={{or (not @model.item.isTimeSeries) (not @model.item.temporalStartDate)}}
            @linkTo={{timeSeriesSelect
              route="time-series.selection"
              addLocation=@model.location.id
              addItem=@model.item.id
            }}
          >
            {{string "item-time-series-link"}}
          </CButton>
        {{else if @model.item.isGeospatial}}
          <CButton @classMix="id-catalog-item__button--map-data"
            @disabled={{not @model.item.isGeospatial}}
            @linkToExternal={{linkToMapForItem @model.item}}
          >
            {{string "item-map-link"}}
          </CButton>
        {{else if @model.item.isBinary}}
          {{#let (or @model.item.binaryFilePath @model.item.spatialOpenDataURL) as | url |}}
            <CButton @external="true"
              @classMix="id-catalog-item__button--download-file"
              @disabled={{not url}}
              @linkToDownload={{url}}
            >
              {{string "item-download-file-link"}}
            </CButton>
          {{/let}}
        {{/if}}

        <div class="id-catalog-item__item">
          <div class="id-catalog-item__item-fields">
            {{! Catalog Record }}
            <CField @label={{string "record-indirect-title-label"}}
              class="id-catalog-item__field--record-title"
              title={{@model.record.title}}
            >
              <LinksTo @route="catalog.record" @model={{@model.record.id}}>
                {{@model.record.title}}
              </LinksTo>
            </CField>
            {{! Generation Effort }}
            <CField @label={{string "record-generation-effort-label"}}
              class="id-catalog-item__field--generation-effort"
              title={{or @model.record.generationEffort (string "record-generation-effort-empty")}}
              @value={{@model.record.generationEffort}}
            />
            {{! Location Name }}
            <CField @label={{string "location-indirect-name-label"}}
              class="id-catalog-item__field--location-name"
              title={{@model.location.name}}
            >
              <LinksTo @route="location-detail" @model={{@model.location.id}}>
                {{@model.location.name}}
              </LinksTo>
            </CField>

            {{! Type }}
            <CField @label={{string "item-type-label"}}
              class="id-catalog-item__field--type"
              title={{@model.item.type}}
              @value={{@model.item.type}}
            />

            {{#if @model.item.isTimeSeries}}

              {{! Modeled/Observed }}
              {{#let (if @model.item.isTimeSeries (titleCase
                (oneString @model.item.isActuallyModeled "item-modeled-value" true "item-observed-value")
              )) as | modelObserved |}}
              <CField @label={{string "item-modeled-label"}}
                class="id-catalog-item__field--modeled"
                title={{or modelObserved "N/A"}}
                @value={{modelObserved}} @empty="[ N/A ]"
              />
              {{/let}}
              {{! Start Date }}
              <CField @label={{string "item-start-date-label"}}
                class="id-catalog-item__field--start-date"
                title={{or @model.item.temporalStartDate (string "item-start-date-empty")}}
                @value={{@model.item.temporalStartDate}}
              />
              {{! End Date }}
              <CField @label={{string "item-end-date-label"}}
                class="id-catalog-item__field--end-date"
                title={{or @model.item.temporalEndDate (string "item-end-date-empty")}}
                @value={{@model.item.temporalEndDate}}
              />
              {{! Parameter Name }}
              <CField @label={{string "item-parameter-name-label"}}
                class="id-catalog-item__field--parameter-name"
                title={{or @model.item.parameter.parameterName (string "item-parameter-name-empty")}}
                @value={{@model.item.parameter.parameterName}}
              />
              {{! Parameter Group }}
              <CField @label={{string "item-parameter-group-label"}}
                class="id-catalog-item__field--parameter-group"
                title={{or @model.item.parameter.parameterGroup (string "item-parameter-group-empty")}}
                @value={{@model.item.parameter.parameterGroup}}
              />
              {{! Parameter Transformation }}
              <CField @label={{string "item-parameter-transformation-label"}}
                class="id-catalog-item__field--transformation"
                title={{or @model.item.parameter.parameterTransformation (string "item-parameter-transformation-empty")}}
                @value={{@model.item.parameter.parameterTransformation}}
              />
              {{! Timestep }}
              <CField @label={{string "item-parameter-timestep-label"}}
                class="id-catalog-item__field--time-step"
                title={{or @model.item.parameter.parameterTimestep (string "item-parameter-timestep-empty")}}
                @value={{@model.item.parameter.parameterTimestep}}
              />
              {{! Parameter Unit }}
              <CField @label={{string "item-parameter-unit-label"}}
                class="id-catalog-item__field--unit"
                title={{or @model.item.parameter.parameterUnit (string "item-parameter-unit-empty")}}
                @value={{@model.item.parameter.parameterUnit}}
              />
              {{! Item Vertical Datum }}
              {{#if @model.item.isTimeSeries}}
                <CField @label={{string "item-vertical-datum-label"}}
                  class="id-catalog-item__field--vertical-datum"
                  title={{or @model.item.verticalDatum (string "item-vertical-datum-empty")}}
                  @value={{@model.item.verticalDatum}}
                />
              {{/if}}

            {{else if @model.item.isGeospatial}}

              {{! Geometry }}
              <CField @label={{string "item-spatial-geometry-label"}}
                class="id-catalog-item__field--geometry"
                title={{or @model.item.spatialGeometry.spatialGeometry (string "item-spatial-geometry-empty")}}
                @value={{@model.item.spatialGeometry.spatialGeometry}}
              />
              {{! Resolution }}
              <CField @label={{string "item-spatial-resolution-label"}}
                class="id-catalog-item__field--resolution"
                title={{or @model.item.spatialResolution.spatialResolution (string "item-spatial-resolution-empty")}}
                @value={{@model.item.spatialResolution.spatialResolution}}
              />
              {{! Transformation }}
              <CField @label={{string "item-spatial-transformation-label"}}
                class="id-catalog-item__field--transformation"
                title={{or @model.item.spatialTransformation.spatialTransformation (string "item-spatial-transformation-empty")}}
                @value={{@model.item.spatialTransformation.spatialTransformation}}
              />
              {{! Item Vertical Datum }}
              {{#if @model.item.isGeospatial}}
                <CField @label={{string "item-vertical-datum-label"}}
                  class="id-catalog-item__field--vertical-datum"
                  title={{or @model.item.verticalDatum (string "item-vertical-datum-empty")}}
                  @value={{@model.item.verticalDatum}}
                />
              {{/if}}

            {{else if @model.item.isBinary}}

              {{! File Type }}
              <CField @label={{string "item-file-format-label"}}
                class="id-catalog-item__field--file-type"
                title={{or @model.item.fileFormat (string "item-file-format-empty")}}
                @value={{@model.item.fileFormat}}
              />
              {{! Publisher }}
              <CField @label={{string "item-publisher-label"}}
                class="id-catalog-item__field--publisher"
                title={{or @model.item.publicationPublisher (string "item-publisher-empty")}}
                @value={{@model.item.publicationPublisher}}
              />
              {{! Publication Date }}
              {{#let (date @model.item.publicationFirstPublicationDate) as | publicationDate |}}
              <CField @label={{string "item-publication-date-label"}}
                class="id-catalog-item__field--publication-date"
                title={{or publicationDate (string "item-publication-date-empty")}}
                @value={{publicationDate}}
              />
              {{/let}}

            {{/if}}

            {{! Update Frequency }}
            <CField @label={{string "item-update-frequency-label"}}
              class="id-catalog-item__field--update-frequency"
              title={{or @model.item.updateFrequency.updatefrequency (string "item-update-frequency-empty")}}
              @value={{@model.item.updateFrequency.updatefrequency}}
            />
            {{! Last Update Date }}
            {{#let (date @model.item.updateDate) as | updateDate |}}
            <CField @label={{string "item-update-date-label"}}
              class="id-catalog-item__field--last-update"
              title={{or updateDate (string "item-update-date-empty")}}
              @value={{updateDate}}
            />
            {{/let}}
          </div>
        </div>

      </div>

      {{! Disclaimer }}
      <div class="id-catalog-item__disclaimer">
        <h4 class="c-section-title id-catalog-item__disclaimer-label">
          {{string "item-disclaimer-heading"}}
        </h4>
        <CField @value={{@model.item.disclaimer}} />
      </div>

      {{! More Items From This Item's Catalog Record }}
      <div class="id-catalog-item__other c-related-links__section">
        <h4 class="c-section-title id-catalog-item__other-label">
          {{string "item-record-items-heading"}}
        </h4>
        <div>
          {{#each this.otherGroupedItems.ordered as | groupedItems |}}
            <ExpandingBlock class="id-catalog-item__other-item-group"
              @initExpanded={{true}} @button={{true}}
              @iconClass="id-catalog-record__item-group-expand-icon">
              <:head>
                <span class="c-related-links__title">{{groupedItems.type}}</span>
              </:head>
              <:body>
              <div class="c-related-links__links">
                {{#each groupedItems.items as | otherItem |}}
                  <div class="c-related-links__link">
                    <LinksTo @route="catalog.record.item" @model={{otherItem.id}}>{{otherItem.title}}</LinksTo>
                  </div>
                {{/each}}
              </div>
              </:body>
            </ExpandingBlock>
          {{else}}
            <CField/>
          {{/each}}
        </div>
      </div>

      {{! Metadata }}
      {{#if false}}
        <div class="id-catalog-item__catalog-meta">
          <h4 class="c-section-title id-catalog-item__catalog-meta-label">
            {{string "item-record-metadata-heading"}}
          </h4>
          <CButton @classMix="id-catalog-item__button--download-catalog-meta"
            @disabled={{true}}
            @title="Not yet implemented">
            {{string "item-download-metadata-link"}}
          </CButton>
        </div>
      {{/if}}
    </div>
    {{outlet}}
  </template>
});
